import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { AsyncSection, Badge } from "../AgentAnalytics/ds";
import { useTableStyles } from "../AgentAnalytics/tableStyles";
import { fetchAnalytics } from "./adTemplateApi";
import { formatLabel, relativeDay } from "./formatters";
import { toLeadRow } from "./publicLeadRow";
import FormatBadge from "./FormatBadge";
import PublicLeadDrawer from "./PublicLeadDrawer";

export const PUBLIC_GENERATED_COLUMNS = [
  { key: "leadName", label: "Lead" },
  { key: "prompt", label: "Prompt" },
  { key: "format", label: "Format" },
  { key: "ads", label: "Ads made" },
  { key: "statusLabel", label: "Status" },
  { key: "createdAt", label: "Created" },
];

export const PUBLIC_GENERATED_EXPORT_COLUMNS = [
  { key: "leadName", label: "Lead" },
  { key: "leadPhone", label: "Phone" },
  { key: "prompt", label: "Prompt" },
  { key: "format", label: "Format" },
  { key: "ads", label: "Ads made" },
  { key: "statusLabel", label: "Status" },
  { key: "createdAt", label: "Created" },
];

const STATUS_LABEL = {
  completed: "Done",
  partial: "Partial",
  failed: "Failed",
  rejected: "Failed",
  pending: "In progress",
  processing: "In progress",
};

const STATUS_TONE = {
  Done: "success",
  Partial: "warning",
  Failed: "error",
};

const useLocalStyles = makeStyles({
  centeredTable: {
    "& td, & th": { textAlign: "center" },
  },
  num: {
    textAlign: "center",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  prompt: {
    maxWidth: 320,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    margin: "0 auto",
  },
});

const toRow = (run) => {
  const lead = run.lead ? toLeadRow(run.lead) : null;
  return {
    ...run,
    lead,
    leadName: (lead && lead.name) || "",
    leadPhone: (lead && lead.phone) || "",
    format: formatLabel(run),
    ads: `${run.delivered || 0} / ${run.requested || 0}`,
    statusLabel: STATUS_LABEL[run.status] || "In progress",
  };
};

export default function PublicGeneratedView({ range, refreshKey, onRows }) {
  const classes = { ...useTableStyles(), ...useLocalStyles() };
  const [state, setState] = useState({
    loading: true,
    notFound: false,
    items: [],
    total: 0,
    error: null,
  });
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    setPage(0);
  }, [range.from, range.to]);

  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    fetchAnalytics("public-leads/generations", {
      from: range.from,
      to: range.to,
      page: page + 1,
    }).then((r) => {
      if (!alive) return;
      const items = ((r.data && r.data.items) || []).map(toRow);
      setState({
        loading: false,
        notFound: r.notFound,
        items,
        total: (r.data && r.data.total) || 0,
        error: r.error,
      });
      if (onRows) onRows(items, PUBLIC_GENERATED_EXPORT_COLUMNS);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, page, refreshKey]);

  return (
    <>
      <AsyncSection
        loading={state.loading}
        notFound={state.notFound}
        error={state.error}
        empty={!state.items.length}
        emptyTitle="No public page generations in this range"
        emptyDescription="Ads made from the public Ads Generator page show up here."
      >
        <Box data-testid="public-generated-table">
          <div className={`${classes.tableWrap} ${classes.centeredTable}`}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {PUBLIC_GENERATED_COLUMNS.map((c) => (
                    <TableCell key={c.key} align="center" className={classes.headCell}>
                      {c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {state.items.map((run) => (
                  <TableRow
                    key={run.templateId}
                    hover
                    className={classes.clickRow}
                    data-testid={`generated-row-${run.templateId}`}
                    onClick={() => run.lead && setSelected(run.lead)}
                  >
                    <TableCell className={classes.cell}>
                      <Typography component="div" className={classes.nameText}>
                        {run.leadName || "—"}
                      </Typography>
                      <Typography component="div" className={classes.monoSub}>
                        {run.leadPhone || run.publicLeadId}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <Typography
                        component="div"
                        className={classes.prompt}
                        title={run.prompt || ""}
                      >
                        {run.prompt || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.cell}>
                      <FormatBadge item={run} />
                    </TableCell>
                    <TableCell className={`${classes.cell} ${classes.num}`}>{run.ads}</TableCell>
                    <TableCell className={classes.cell}>
                      <Badge tone={STATUS_TONE[run.statusLabel] || "neutral"} subtle>
                        {run.statusLabel}
                      </Badge>
                    </TableCell>
                    <TableCell className={classes.cell}>{relativeDay(run.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <TablePagination
            component="div"
            count={state.total}
            page={page}
            onChangePage={(event, next) => setPage(next)}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
          />
        </Box>
      </AsyncSection>

      <PublicLeadDrawer lead={selected} onClose={() => setSelected(null)} />
    </>
  );
}
